import React, { Fragment, FunctionComponent } from "react";
import { Box, Typography } from "@mui/material";

interface MarkdownTextProps {
	text: string;
}

const renderInline = (text: string) => {
	const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g);
	return parts.map((part, index) => {
		if (part.startsWith("**") && part.endsWith("**")) {
			return <strong key={index}>{part.slice(2, -2)}</strong>;
		}
		if (part.startsWith("*") && part.endsWith("*") && part.length > 2) {
			return <em key={index}>{part.slice(1, -1)}</em>;
		}
		if (part.startsWith("`") && part.endsWith("`") && part.length > 2) {
			return (
				<Box
					key={index}
					component="code"
					sx={{
						backgroundColor: "rgba(0, 0, 0, 0.08)",
						borderRadius: "4px",
						paddingInline: "4px",
						fontSize: "0.9em",
					}}
				>
					{part.slice(1, -1)}
				</Box>
			);
		}
		return <Fragment key={index}>{part}</Fragment>;
	});
};

const MarkdownText: FunctionComponent<MarkdownTextProps> = ({ text }) => {
	const lines = text.split("\n");
	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
			{lines.map((line, index) => {
				const trimmed = line.trim();
				if (!trimmed) return null;

				const headingMatch = trimmed.match(/^(#{1,6})\s+(.*)$/);
				if (headingMatch) {
					return (
						<Typography
							key={index}
							variant="body2"
							sx={{ fontWeight: "bold" }}
						>
							{renderInline(headingMatch[2])}
						</Typography>
					);
				}

				const bulletMatch = trimmed.match(/^[-*]\s+(.*)$/);
				const numberedMatch = trimmed.match(/^(\d+[.)])\s+(.*)$/);
				if (bulletMatch || numberedMatch) {
					return (
						<Box
							key={index}
							sx={{ display: "flex", gap: "6px", paddingLeft: "8px" }}
						>
							<Typography variant="body2">
								{bulletMatch ? "•" : numberedMatch[1]}
							</Typography>
							<Typography variant="body2">
								{renderInline(bulletMatch ? bulletMatch[1] : numberedMatch[2])}
							</Typography>
						</Box>
					);
				}

				return (
					<Typography key={index} variant="body2">
						{renderInline(trimmed)}
					</Typography>
				);
			})}
		</Box>
	);
};

export default MarkdownText;